import React, { useSyncExternalStore } from "react";
import catalog from "./catalog.json";
import { getInitialBrowserLocale, LOCALE_STORAGE_KEY, normalizeLocale } from "./locales";

let currentLocale = "en";
const listeners = new Set();
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getLocale = () => currentLocale;
export const useLocale = () => useSyncExternalStore(subscribe, getLocale, () => "en");

export function setLocale(value, { persist = true } = {}) {
  currentLocale = normalizeLocale(value);
  if (typeof document !== "undefined") {
    document.documentElement.lang = currentLocale;
    document.documentElement.dir = currentLocale === "ar" ? "rtl" : "ltr";
  }
  if (persist) {
    try { window.localStorage.setItem(LOCALE_STORAGE_KEY, currentLocale); } catch { /* Optional storage. */ }
  }
  listeners.forEach((listener) => listener());
  return currentLocale;
}

// Called once by the browser entrypoint, never by a render or a catalog import.
export const initializeLocale = () => setLocale(getInitialBrowserLocale(), { persist: false });

const directionSafeText = (locale, text) => locale === "ar"
  ? text.replace(/10k Science(?: Edu)?|Meta Quest/g, (name) => `\u2066${name.replace(/ /g, "\u00a0")}\u2069`)
  : text;

export function translateForLocale(locale, message, values = {}) {
  const translated = catalog[normalizeLocale(locale)]?.[message] ?? message;
  return directionSafeText(locale, translated.replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name] ?? "") : match));
}

// Source English is the message key (gettext style). Only site-owned copy belongs here.
export const translate = (message, values) => translateForLocale(currentLocale, message, values);

// Rich messages keep links/emphasis and dynamic React values intact and escapable.
export function translateRich(message, values = {}) {
  const translated = directionSafeText(currentLocale, catalog[currentLocale]?.[message] ?? message);
  return translated.split(/(\{\w+\})/g).map((part, index) => {
    const name = /^\{(\w+)\}$/.exec(part)?.[1];
    const value = name && Object.prototype.hasOwnProperty.call(values, name) ? values[name] : part;
    return React.isValidElement(value) ? React.cloneElement(value, { key: index }) : value;
  });
}

export const formatNumber = (value, options) => new Intl.NumberFormat(currentLocale, options).format(value);
export const formatDate = (value, options) => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat(currentLocale, options).format(date);
};
